/**
 * v4 PWA install prompt.
 *
 * Mobile-only bottom sheet that appears once per browser session when the
 * app is installable. Listens for the standard `beforeinstallprompt` event
 * on Chrome/Edge/Android. On iOS Safari (which doesn't fire that event)
 * shows manual "Share → Add to Home Screen" instructions when the user
 * is on iOS, mobile, in a regular browser tab.
 */

import { useEffect, useState } from 'react';
import { X, Smartphone, Share } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'client-install-dismissed';

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosVisible, setIosVisible] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Already dismissed this session? do nothing.
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') return;
    } catch {}

    // Already installed as a PWA → don't prompt.
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if ((navigator as any).standalone === true) return; // iOS

    const isMobile = window.matchMedia('(max-width: 820px)').matches;
    if (!isMobile) return;

    // Chrome / Android path
    const onBip = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onBip);

    // iOS path - Safari never fires beforeinstallprompt.
    const ua = navigator.userAgent.toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(ua);
    const isSafari = /safari/.test(ua) && !/crios|fxios|edgios/.test(ua);
    if (isIos && isSafari) {
      // Show iOS-specific instructions after a short delay so it doesn't
      // pop up immediately on first load.
      const timer = setTimeout(() => { setIosVisible(true); setShow(true); }, 3500);
      return () => { clearTimeout(timer); window.removeEventListener('beforeinstallprompt', onBip); };
    }

    return () => window.removeEventListener('beforeinstallprompt', onBip);
  }, []);

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch {}
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } catch {}
    dismiss();
  };

  if (!show) return null;

  return (
    <>
      <div onClick={dismiss} className="fixed inset-0 z-[60] bg-foreground/40 backdrop-blur-sm animate-in fade-in duration-200" />
      <div className="fixed inset-x-0 bottom-0 z-[70] mx-auto max-w-[720px] rounded-t-2xl border-t border-border bg-card p-5 shadow-2xl animate-in slide-in-from-bottom duration-300">
        <div className="mx-auto mb-4 h-1 w-12 rounded-full bg-muted" />
        <div className="flex items-start gap-3 mb-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-[var(--v4-accent)] text-white">
            <Smartphone className="h-6 w-6" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-foreground">Install RoniDoc on your phone</h2>
            <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
              {iosVisible
                ? 'Get one-tap access from your home screen - no app store, no sign-up.'
                : 'Faster access. Works offline. Notifications when your firm sends a message.'}
            </p>
          </div>
          <button onClick={dismiss} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        {iosVisible ? (
          // iOS Safari needs manual instructions
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <p className="text-xs leading-relaxed text-foreground/90">
              1. Tap <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-card border border-border align-middle"><Share className="h-3 w-3" strokeWidth={1.75} /></span> Share at the bottom of Safari
              <br/>
              2. Scroll down and tap <span className="font-semibold">Add to Home Screen</span>
              <br/>
              3. Tap <span className="font-semibold">Add</span>
            </p>
            <button onClick={dismiss} className="mt-3 w-full rounded-md border border-border bg-card py-2 text-xs font-semibold text-muted-foreground hover:bg-muted">
              Got it
            </button>
          </div>
        ) : (
          // Chrome / Android - programmatic prompt
          <div className="flex gap-2">
            <button
              onClick={dismiss}
              className="flex-1 rounded-md border border-border bg-card py-2.5 text-sm font-semibold text-muted-foreground hover:bg-muted"
            >
              Not now
            </button>
            <button
              onClick={install}
              className="flex-1 rounded-md bg-[var(--v4-accent)] py-2.5 text-sm font-semibold text-white hover:bg-[var(--v4-accent-hover)]"
            >
              Install
            </button>
          </div>
        )}
      </div>
    </>
  );
}
